// ROI Reporting — links measured biometric "then vs now" change to a
// defensible, cited £ value per metric (sleep duration, sleep quality,
// resting heart rate, HRV, vigorous activity), plus a derived workplace-
// injuries saving and a £0 staff-retention placeholder pending evidence.
//
// Central-admin gated (see router.tsx / AdminSideNav centralOnly). Pure
// client-side calculator in ../data/model.ts — no backend calls. Built with
// shadcn/ui (scoped Tailwind, see src/styles/roi-shadcn.css) rather than MUI —
// flat, bordered, no glow or motion.

import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { BookOpen, SlidersHorizontal, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEFAULT_COMPANY_INPUTS, DEFAULT_LEVER_INPUTS, computeRoi } from '../data/model';
import type { RoiCompanyInputs, RoiLeverId, RoiLeverInputs } from '../data/model';
import { AssumptionsSection } from '../sections/AssumptionsSection';
import { SavingsSection } from '../sections/SavingsSection';
import { MethodologySection } from '../sections/MethodologySection';
import '../../../../../styles/roi-shadcn.css';

type SectionKey = 'assumptions' | 'savings' | 'methodology';

const NAV: { key: SectionKey; label: string; icon: React.ReactElement }[] = [
  { key: 'assumptions', label: 'Company Numbers', icon: <SlidersHorizontal className="size-4" /> },
  { key: 'savings', label: 'Projected ROI', icon: <Wallet className="size-4" /> },
  { key: 'methodology', label: 'Methodology & Sources', icon: <BookOpen className="size-4" /> },
];

export const RoiReporting: React.FC = () => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [section, setSection] = useState<SectionKey>('savings');
  const [company, setCompany] = useState<RoiCompanyInputs>(DEFAULT_COMPANY_INPUTS);
  const [leverInputs, setLeverInputs] = useState<RoiLeverInputs>(DEFAULT_LEVER_INPUTS);

  const result = useMemo(() => computeRoi(company, leverInputs), [company, leverInputs]);

  const handleLeverInputChange = (id: RoiLeverId, field: 'then' | 'now', value: number) => {
    setLeverInputs((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleReset = () => {
    setCompany(DEFAULT_COMPANY_INPUTS);
    setLeverInputs(DEFAULT_LEVER_INPUTS);
  };

  return (
    <div className={cn('roi-page', isDark && 'dark')}>
      <div className="flex flex-col gap-5 p-6">
        {/* Title and section switcher share a row so the sections themselves get
            the page's full width — a 220px side rail spent on three links left
            the content cramped on wide screens. */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold">ROI Reporting</h1>
            <p className="text-sm text-muted-foreground">
              Metric-linked ROI model — each figure traces back to one published study plus a stated assumption.
            </p>
          </div>

          <nav aria-label="ROI Reporting sections">
            <ul className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1.5">
              {NAV.map((item) => {
                const active = item.key === section;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => setSection(item.key)}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        active
                          ? 'bg-secondary text-secondary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className="min-w-0">
          {section === 'assumptions' && (
            <AssumptionsSection company={company} onChange={setCompany} onReset={handleReset} />
          )}
          {section === 'savings' && (
            <SavingsSection
              leverInputs={leverInputs}
              onLeverInputChange={handleLeverInputChange}
              result={result}
              onEditCompanyNumbers={() => setSection('assumptions')}
            />
          )}
          {section === 'methodology' && <MethodologySection />}
        </div>
      </div>
    </div>
  );
};
